import { useQuery } from "@tanstack/react-query";
import {
  getContributionPriority,
  type ContributionPriorityResponse,
  type ContributionRung,
} from "../../lib/api-modules/portfolio.js";
import { Card, Pill } from "../design-system/index.js";
import { formatCentsCompact } from "../../lib/util/money.js";

/**
 * Phase 13 — Contribution prioritization dashboard tile.
 *
 * Renders the household's "where should the next dollar go?" ranked
 * list with progress bars per rung. Top recommendation pinned at the
 * top with a callout. Citations live on each rung's tooltip via the
 * underlying API response.
 */

const RUNG_LABEL_DISPLAY: Record<ContributionRung["label"], string> = {
  "401k_to_employer_match": "401(k) employer match",
  "hsa_to_limit": "HSA",
  "roth_ira_to_limit": "Roth IRA",
  "401k_traditional_to_limit": "401(k) traditional",
  "mega_backdoor": "Mega backdoor",
  "taxable": "Taxable brokerage",
};

export function ContributionPrioritizationTile() {
  const { data, isLoading, error } = useQuery<ContributionPriorityResponse>({
    queryKey: ["portfolio", "contribution-priority"],
    queryFn: getContributionPriority,
  });

  if (isLoading) {
    return (
      <Card>
        <h3 className="mb-3 font-display text-lg font-medium text-[var(--color-text)]">
          Where should the next dollar go?
        </h3>
        <p className="text-sm text-[var(--color-muted)]">Loading…</p>
      </Card>
    );
  }
  if (error || !data) {
    return (
      <Card>
        <h3 className="mb-3 font-display text-lg font-medium text-[var(--color-text)]">
          Where should the next dollar go?
        </h3>
        <p className="text-sm text-[var(--color-danger)]">
          {error instanceof Error ? error.message : "Couldn't load."}
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="mb-3 flex items-start justify-between gap-3">
        <h3 className="font-display text-lg font-medium text-[var(--color-text)]">
          Where should the next dollar go?
        </h3>
        {data.limits_stale && (
          <Pill tone="warning">IRS limits {data.limits_as_of} (stale)</Pill>
        )}
      </div>
      {data.next_dollar && data.next_dollar.is_eligible && (
        <div className="mb-4 rounded-md border border-[var(--color-accent)]/30 bg-[var(--color-accent)]/5 p-3">
          <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-accent)]">
            Next dollar
          </p>
          <p className="mt-1 text-sm text-[var(--color-text)]">
            {data.next_dollar.headline}
          </p>
          {data.next_dollar.remaining_capacity_cents > 0 &&
            Number.isFinite(data.next_dollar.remaining_capacity_cents) && (
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                Remaining capacity:{" "}
                <strong className="text-[var(--color-text)]">
                  {formatCentsCompact(
                    data.next_dollar.remaining_capacity_cents,
                  )}
                </strong>
              </p>
            )}
        </div>
      )}
      <ul className="space-y-3">
        {data.rungs.map((rung, idx) => (
          <RungRow key={rung.label} rung={rung} index={idx + 1} />
        ))}
      </ul>
      <p className="mt-3 text-xs text-[var(--color-muted)]">
        IRS limits as of {data.limits_as_of}. Tax year {data.tax_year}.
      </p>
    </Card>
  );
}

function RungRow({ rung, index }: { rung: ContributionRung; index: number }) {
  const tone: "success" | "warning" | "danger" | "neutral" = rung.is_eligible
    ? rung.remaining_capacity_cents > 0
      ? "warning"
      : "success"
    : "neutral";
  const limit = Number.isFinite(rung.annual_limit_cents)
    ? rung.annual_limit_cents
    : null;
  const remaining = Number.isFinite(rung.remaining_capacity_cents)
    ? rung.remaining_capacity_cents
    : null;
  const ytd = rung.ytd_contributed_cents;
  // Progress bar: filled = ytd, total = limit. When limit is Infinity
  // (taxable), show a single neutral row.
  const pct =
    limit !== null && limit > 0
      ? Math.min(100, Math.round((ytd / limit) * 100))
      : null;
  return (
    <li>
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-[var(--color-text)]">
            <span className="text-[var(--color-muted)]">{index}. </span>
            {RUNG_LABEL_DISPLAY[rung.label]}
            {!rung.is_eligible && (
              <span className="ml-2 text-xs italic text-[var(--color-muted)]">
                — {rung.reason_if_ineligible ?? "not eligible"}
              </span>
            )}
          </p>
          {rung.is_eligible && limit !== null && (
            <p className="mt-1 text-xs text-[var(--color-muted)]">
              {formatCentsCompact(ytd)} of {formatCentsCompact(limit)} contributed
              {remaining !== null && remaining > 0 && (
                <>
                  {" "}— {formatCentsCompact(remaining)} remaining
                </>
              )}
            </p>
          )}
        </div>
        {rung.is_eligible && pct !== null && (
          <Pill tone={pct >= 100 ? "success" : "warning"}>
            {pct}%
          </Pill>
        )}
      </div>
      {rung.is_eligible && pct !== null && (
        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[var(--color-elevated)]">
          <div
            className="h-full rounded-full bg-[var(--color-accent)] transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      {/* Suppress unused var lint */}
      <span className="hidden">{tone}</span>
    </li>
  );
}
